import type { CostRollup } from "@/lib/api";
import { formatDurationMs } from "@/lib/utils";

export function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export type StatusBadgeVariant =
  | "default"
  | "secondary"
  | "outline"
  | "destructive"
  | "success"
  | "warning"
  | "info";

/**
 * Color map for case / version / pipeline / asset / llm status badges.
 * success = live or done, warning = in progress, info = pending,
 * destructive = failed, outline = draft/idle.
 */
export function statusBadgeVariant(status: string): StatusBadgeVariant {
  switch (status) {
    case "published":
    case "succeeded":
    case "ready":
    case "replaced":
    case "supported":
      return "success";
    case "failed":
    case "unsupported":
    case "error":
      return "destructive";
    case "running":
    case "generating":
    case "queued":
    case "waiting_human":
      return "warning";
    case "pending":
    case "planned":
    case "partial":
    case "warn":
      return "info";
    case "draft":
    case "superseded":
    case "skipped":
    case "cancelled":
    default:
      return "outline";
  }
}

const VERSION_STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  published: "已发布",
  superseded: "已替换",
  failed: "失败",
};

export function formatVersionStatus(status: string): string {
  return VERSION_STATUS_LABEL[status] ?? status;
}

const CASE_STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  published: "已发布",
};

export function formatCaseStatus(status: string): string {
  return CASE_STATUS_LABEL[status] ?? status;
}

export function stepBadgeVariant(status: string): StatusBadgeVariant {
  return statusBadgeVariant(status);
}

export function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  if (n === 0) return "$0";
  if (n < 0.0001) return `$${n.toExponential(2)}`;
  return `$${n.toFixed(4)}`;
}

export function formatCostRollup(rollup: CostRollup | undefined): string | null {
  if (!rollup || rollup.stepCount === 0) return null;
  return `cost ${formatUsd(rollup.totalUsd)} · ${rollup.succeededSteps}/${rollup.stepCount} steps`;
}

export function formatStepDuration(durationMs: number | null | undefined): {
  label: string;
  detail: string | null;
} {
  if (durationMs == null) {
    return { label: "-", detail: null };
  }
  return { label: formatDurationMs(durationMs), detail: `${durationMs} ms` };
}

export function versionLabel(v: {
  locale: string;
  versionN: number;
  status: string;
  branchLabel?: string | null;
}): string {
  const branch = v.branchLabel ? ` · ${v.branchLabel}` : "";
  return `${v.locale} · v${v.versionN} · ${formatVersionStatus(v.status)}${branch}`;
}
